import { DetailedLoanCard } from './DetailedLoanCard';
import { useTrustpilotRating } from '@/hooks/useTrustpilotRatings';
import { useLanguage } from '@/contexts/LanguageContext';
import { Suspense } from 'react';

interface DynamicLoanProduct {
  provider: string;
  logo: string;
  loanAmount: string;
  nominalRate: string;
  effectiveRate: string;
  loanTerm: string;
  loanWithoutUC: string;
  responseTime: string;
  paymentSolution: string;
  link: string;
  lenderBadge?: string;
  // Trustpilot info
  entityType: string;
  entitySlug: string;
  fallbackRating: number;
  fallbackReviewCount: number;
}

interface DynamicLoanCardProps {
  product: DynamicLoanProduct;
}

function DynamicLoanCard({ product }: DynamicLoanCardProps) {
  const { data: trustpilotData } = useTrustpilotRating(product.entityType, product.entitySlug);

  return (
    <DetailedLoanCard
      provider={product.provider}
      logo={product.logo}
      loanAmount={product.loanAmount}
      nominalRate={product.nominalRate}
      effectiveRate={product.effectiveRate}
      loanTerm={product.loanTerm}
      loanWithoutUC={product.loanWithoutUC}
      responseTime={product.responseTime}
      paymentSolution={product.paymentSolution}
      link={product.link}
      rating={trustpilotData?.rating ?? product.fallbackRating}
      reviewCount={trustpilotData?.reviewCount ?? product.fallbackReviewCount}
      lenderBadge={product.lenderBadge}
    />
  );
}

interface DynamicVirtualizedProductListProps {
  products: DynamicLoanProduct[];
}

/**
 * Product list with dynamic Trustpilot ratings from database
 */
export const DynamicVirtualizedProductList = ({ 
  products
}: DynamicVirtualizedProductListProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {products.map((product, idx) => (
        <Suspense
          key={`${product.provider}-${idx}`}
          fallback={
            <div className="h-[420px] bg-muted/30 animate-pulse rounded-lg" />
          }
        >
          <DynamicLoanCard product={product} />
        </Suspense>
      ))}
    </div>
  );
};
